import { RetryPolicySchema } from '../../rules/schemas/connection-rules.schema';
import { AgentConfigSchema } from '../agents/agent-config.schema';
import { retryDelayMs, type TRetrySchedulePolicy } from '../../retry/services/retry-schedule';
import {
  ENodeType,
  FLOW_GRAPH_SCHEMA_VERSION,
  FLOW_LIMITS,
  WAIT_MAIN_PORT,
} from '../constants/flow.constants';
import type { INodeRefSpec, IUnconnectedBehaviour } from '../registry/node-types.registry';
import { FlowConditionFormSchema, type TFlowConditionForm } from '../schemas/flow-condition.schema';
import {
  FlowGraphSchema,
  type TFlowGraphInput,
  type TFlowNode,
} from '../schemas/flow-graph.schema';
import {
  buildDominance,
  buildGraphIndex,
  descendants,
  edgeFanoutNodes,
  enclosingLoops,
  isJoin,
  joinReach,
  pairedSplit,
  ROOT_SCOPE,
  scopeEntries,
  scopeExits,
  successors,
  topoOrder,
  type IGraphIndex,
} from './flow-graph.analysis';
import { compileConditionForm } from './flow-condition.compiler';
import { collectTemplateRefs, parseContextPath } from './flow-template';

export interface ICompiledTarget {
  to: string;
  input: string;
  handled: boolean;
}

export interface ICompiledCondition {
  field: string;
  output?: string;
  /** Nodes run `form` through the tri-state evaluator (§5.2); `searchDsl` is for search only. */
  form: TFlowConditionForm;
  searchDsl: Record<string, unknown>;
}

export interface ICompiledJoin {
  type: 'merge' | 'aggregate';
  /** Edge fan-out nodes whose `e:` records a copy sheds at the tail of its lineage (§4.5). */
  strip: string[];
  split: string | null;
  loops: string[];
}

/** Everything the engine would otherwise recompute per copy (automation-architecture §9.3). */
export interface ICompiledFlowGraph {
  schema: typeof FLOW_GRAPH_SCHEMA_VERSION;
  trigger: string | null;
  topoOrder: string[];
  loops: Record<
    string,
    { entry: string | null; exit: string | null; exitOutput: string | null; order: string[] }
  >;
  /** node → output → edges; the first target keeps the copy, the rest get children (§4.4). */
  outgoing: Record<string, Record<string, ICompiledTarget[]>>;
  unconnected: Record<string, Record<string, IUnconnectedBehaviour>>;
  passThrough: Record<string, string>;
  /** node → the `join:input` pairs a copy standing on it can still reach (§4.5). */
  reach: Record<string, string[]>;
  joinWatchers: string[];
  joins: Record<string, ICompiledJoin>;
  dominators: Record<string, string[]>;
  templateRefs: Record<string, string[]>;
  /** node → outputs to load when a copy resumes here: refs of every step still ahead (§9.2). */
  resumeRefs: Record<string, string[]>;
  conditions: Record<string, ICompiledCondition[]>;
  refs: (INodeRefSpec & { nodeKey: string })[];
  /** §9.5 copy lifetime (default deliver policy): past it a run is stuck (§7.4). */
  lifetimeSec?: number;
}

export function compileFlowGraph(graph: TFlowGraphInput): ICompiledFlowGraph {
  const index = buildGraphIndex(FlowGraphSchema.parse(graph));
  const dominance = buildDominance(index);

  const outgoing: ICompiledFlowGraph['outgoing'] = {};
  const unconnected: ICompiledFlowGraph['unconnected'] = {};
  const passThrough: ICompiledFlowGraph['passThrough'] = {};
  for (const [key, node] of index.nodes) {
    outgoing[key] = {};
    unconnected[key] = {};
    const specs = index.outputs.get(key) ?? [];
    const main = specs.find((spec) => spec.main)?.key;
    if (node.disabled && main && index.descriptors.get(key)?.disableable) passThrough[key] = main;
    for (const spec of specs) {
      const edges = (index.out.get(key) ?? []).filter((edge) => edge.output === spec.key);
      if (edges.length === 0) unconnected[key][spec.key] = spec.unconnected;
      else {
        outgoing[key][spec.key] = edges.map((edge) => ({
          to: edge.to,
          input: edge.input,
          handled: edge.handled === true,
        }));
      }
    }
  }

  const loops: ICompiledFlowGraph['loops'] = {};
  for (const [key, node] of index.nodes) {
    if (node.type !== ENodeType.LOOP) continue;
    const entries = scopeEntries(index, key);
    const exits = scopeExits(index, key);
    const exit = exits.length === 1 ? exits[0] : null;
    const exitOutput = exit ? (index.outputs.get(exit)?.find((s) => s.main)?.key ?? null) : null;
    loops[key] = {
      entry: entries.length === 1 ? entries[0] : null,
      exit,
      exitOutput,
      order: topoOrder(index, key).order,
    };
    // In a body nothing ends: the exit's main output ends the round, anything else is §5.4's
    // "copy fell off inside the body", which the loop turns into its own error.
    for (const member of index.scopeMembers.get(key) ?? []) {
      for (const [output, behaviour] of Object.entries(unconnected[member])) {
        const roundEnd = member === exit && output === exitOutput;
        unconnected[member][output] = {
          ...behaviour,
          label: roundEnd ? 'end of round' : `leaves ${key} by error`,
          loop: { key, exit: roundEnd ? 'round_end' : 'error' },
        };
      }
    }
  }

  const reachMap = joinReach(index);
  const reach = Object.fromEntries(reachMap);
  const fanouts = edgeFanoutNodes(index);
  const joins: ICompiledFlowGraph['joins'] = {};
  for (const [key, node] of index.nodes) {
    if (!isJoin(index, key)) continue;
    const strip = [...fanouts].filter((f) => reachMap.get(f)?.some((p) => p.startsWith(`${key}:`)));
    const aggregate = node.type === ENodeType.AGGREGATE;
    joins[key] = {
      type: aggregate ? 'aggregate' : 'merge',
      strip: strip.sort(),
      split: aggregate ? pairedSplit(index, dominance, key) : null,
      loops: enclosingLoops(index, key).reverse(),
    };
  }

  const templateRefs: Record<string, string[]> = {};
  const conditions: Record<string, ICompiledCondition[]> = {};
  const refs: ICompiledFlowGraph['refs'] = [];
  for (const [key, node] of index.nodes) {
    templateRefs[key] = referencedNodes(index, node);
    const descriptor = index.descriptors.get(key);
    if (!descriptor) continue;
    for (const spec of descriptor.refs(node.config ?? {})) refs.push({ ...spec, nodeKey: key });
    const compiled: ICompiledCondition[] = [];
    for (const spec of descriptor.conditions(node.config ?? {})) {
      const form = FlowConditionFormSchema.safeParse(spec.form);
      if (!form.success) continue;
      compiled.push({
        field: spec.field,
        output: spec.output,
        form: form.data,
        searchDsl: compileConditionForm(form.data),
      });
    }
    if (compiled.length > 0) conditions[key] = compiled;
  }

  const resumeRefs: Record<string, string[]> = {};
  for (const key of index.nodes.keys()) {
    const ahead = remainingNodes(index, key);
    resumeRefs[key] = [...new Set([...ahead].flatMap((k) => templateRefs[k] ?? []))].sort();
  }

  const dominators: Record<string, string[]> = {};
  for (const key of index.nodes.keys()) dominators[key] = [...dominance.visible(key)].sort();

  return {
    schema: FLOW_GRAPH_SCHEMA_VERSION,
    trigger: index.trigger,
    topoOrder: topoOrder(index, ROOT_SCOPE).order,
    loops,
    outgoing,
    unconnected,
    passThrough,
    reach,
    joinWatchers: Object.keys(reach).sort(),
    joins,
    dominators,
    templateRefs,
    resumeRefs,
    conditions,
    refs,
    lifetimeSec: longestInScope(index, ROOT_SCOPE, {}).seconds,
  };
}

export function targets(
  compiled: ICompiledFlowGraph,
  node: string,
  output: string,
): ICompiledTarget[] {
  const direct = compiled.outgoing[node]?.[output];
  if (direct) return direct;
  // Item 2 legacy alias: a stored `.compiled` snapshot from before the wait-port rename may
  // still key the wait node's edges under "main" — the migration rewrites these, this is belt
  // and braces for any that were missed.
  if (output === WAIT_MAIN_PORT) return compiled.outgoing[node]?.['main'] ?? [];
  return [];
}

export function combinesResults(node: Pick<TFlowNode, 'type' | 'config'>): boolean {
  return (
    node.type === ENodeType.MERGE &&
    node.config?.mode === 'combine' &&
    node.config?.combines === 'results'
  );
}

// Combine → results (§4.5): the answer of whichever feeding step the arrived copy passed.
export function mergeInputAnswer(
  compiled: ICompiledFlowGraph,
  merge: string,
  input: string,
  nodes: Readonly<Record<string, unknown>>,
): unknown {
  for (const [from, outputs] of Object.entries(compiled.outgoing)) {
    const feeds = Object.values(outputs).some((targets) =>
      targets.some((target) => target.to === merge && target.input === input),
    );
    if (feeds && Object.hasOwn(nodes, from)) return nodes[from];
  }
  return null;
}

function referencedNodes(index: IGraphIndex, node: TFlowNode): string[] {
  const keys = new Set<string>();
  if (combinesResults(node)) {
    for (const edge of index.in.get(node.key) ?? []) keys.add(edge.from);
  }
  const descriptor = index.descriptors.get(node.key);
  const { refs } = collectTemplateRefs(
    node.config ?? {},
    descriptor?.templateFields ?? [],
    descriptor?.templateRoots,
  );
  for (const ref of refs) {
    if (ref.nodeKey) keys.add(ref.nodeKey);
  }
  for (const spec of descriptor?.contextPaths(node.config ?? {}) ?? []) {
    try {
      const parsed = parseContextPath(spec.path);
      if (parsed.nodeKey) keys.add(parsed.nodeKey);
    } catch {
      continue;
    }
  }
  return [...keys].sort();
}

function bodyAll(index: IGraphIndex, loop: string): Set<string> {
  const all = new Set<string>();
  for (const member of index.scopeMembers.get(loop) ?? []) {
    all.add(member);
    if (index.nodes.get(member)?.type === ENodeType.LOOP) {
      for (const inner of bodyAll(index, member)) all.add(inner);
    }
  }
  return all;
}

function remainingNodes(index: IGraphIndex, key: string): Set<string> {
  const set = new Set<string>([key, ...descendants(index, key)]);
  const loop = index.parentLoop.get(key);
  // A copy inside a loop body may start the body again, then continue after the loop.
  if (loop !== undefined) {
    for (const k of bodyAll(index, loop)) set.add(k);
    for (const k of remainingNodes(index, loop)) set.add(k);
  }
  for (const k of [...set]) {
    if (index.nodes.get(k)?.type === ENodeType.LOOP)
      for (const inner of bodyAll(index, k)) set.add(inner);
  }
  return set;
}

export interface ICopyLifetime {
  seconds: number;
  path: string[];
}

export interface ILifetimeOptions {
  deliverRetry?: (destinationId: string) => TRetrySchedulePolicy | undefined;
  deliverTimeoutSec?: (destinationId: string) => number | undefined;
}

const CONNECTION_DEFAULT_RETRY: TRetrySchedulePolicy = RetryPolicySchema.parse({});

export function copyLifetime(
  graph: TFlowGraphInput,
  options: ILifetimeOptions = {},
): ICopyLifetime {
  // Parsed: a stored retry lacks the schema defaults (jitterPct, strategy) the sum needs.
  const index = buildGraphIndex(FlowGraphSchema.parse(graph));
  return longestInScope(index, ROOT_SCOPE, options);
}

export interface ILongestHold {
  seconds: number;
  nodeKey: string | null;
  nodeName: string | null;
  retries: boolean;
}

export function longestHold(graph: TFlowGraphInput, options: ILifetimeOptions = {}): ILongestHold {
  const index = buildGraphIndex(FlowGraphSchema.parse(graph));
  const lifetime = longestInScope(index, ROOT_SCOPE, options);
  let best: { key: string; seconds: number } | null = null;
  for (const key of lifetime.path) {
    const seconds = ownSeconds(index, key, options);
    if (seconds > 0 && (!best || seconds > best.seconds)) best = { key, seconds };
  }
  const node = best ? index.nodes.get(best.key) : undefined;
  return {
    seconds: lifetime.seconds,
    nodeKey: node?.key ?? null,
    nodeName: node?.name ?? null,
    retries: node ? Boolean(index.descriptors.get(node.key)?.retries) : false,
  };
}

function longestInScope(
  index: IGraphIndex,
  scope: string,
  options: ILifetimeOptions,
): ICopyLifetime {
  const entries = scopeEntries(index, scope);
  if (entries.length === 0) return { seconds: 0, path: [] };
  const best = new Map<string, ICopyLifetime>();
  const order = topoOrder(index, scope).order;
  for (const key of [...order].reverse()) {
    const own = ownSeconds(index, key, options);
    let tail: ICopyLifetime = { seconds: 0, path: [] };
    for (const next of successors(index, key)) {
      const candidate = best.get(next);
      if (candidate && candidate.seconds > tail.seconds) tail = candidate;
    }
    best.set(key, { seconds: own + tail.seconds, path: [key, ...tail.path] });
  }
  return entries
    .map((entry) => best.get(entry) ?? { seconds: 0, path: [entry] })
    .reduce((a, b) => (b.seconds > a.seconds ? b : a));
}

function ownSeconds(index: IGraphIndex, key: string, options: ILifetimeOptions): number {
  const node = index.nodes.get(key)!;
  const config = node.config ?? {};
  const descriptor = index.descriptors.get(key);
  if (node.disabled) return 0;
  switch (node.type) {
    case ENodeType.WAIT:
      if (config.mode === 'duration' && typeof config.durationSec === 'number') {
        return config.durationSec;
      }
      return typeof config.maxWaitSec === 'number'
        ? config.maxWaitSec
        : FLOW_LIMITS.defaultUntilWaitSec;
    case ENodeType.MERGE:
    case ENodeType.AGGREGATE:
      return typeof config.timeoutSec === 'number'
        ? config.timeoutSec
        : FLOW_LIMITS.defaultJoinTimeoutSec;
    case ENodeType.LOOP: {
      const iterations =
        typeof config.maxIterations === 'number'
          ? config.maxIterations
          : FLOW_LIMITS.defaultLoopIterations;
      return longestInScope(index, key, options).seconds * iterations;
    }
    case ENodeType.AI_AGENT:
      return agentSeconds(config, node.retry ?? descriptor?.defaultRetry ?? null);
    default:
      break;
  }
  if (!descriptor?.retries) return 0;
  const destinationId = String(config.destinationId ?? '');
  const deliver = node.type === ENodeType.DELIVER;
  const policy =
    node.retry ??
    (deliver
      ? (options.deliverRetry?.(destinationId) ?? CONNECTION_DEFAULT_RETRY)
      : descriptor.defaultRetry);
  if (!policy) return 0;
  const timeout =
    (deliver ? options.deliverTimeoutSec?.(destinationId) : undefined) ??
    descriptor.attemptTimeoutSec ??
    0;
  return Math.ceil(worstPausesSec(policy) + policy.maxAttempts * timeout);
}

// agents §3.3: its active time, plus the longest wait a step can hold × steps, plus pauses.
function agentSeconds(config: Record<string, unknown>, retry: TRetrySchedulePolicy | null): number {
  const parsed = AgentConfigSchema.safeParse(config);
  if (!parsed.success) return 0;
  const { limits, tools } = parsed.data;
  const longestWait = Math.max(
    0,
    ...tools.map((tool) =>
      tool.kind === 'run_flow' || tool.kind === 'hookpipe' ? tool.config.waitSec : 0,
    ),
  );
  const pauses = retry ? worstPausesSec(RetryPolicySchema.parse({ ...retry })) : 0;
  return Math.ceil(limits.maxActiveSec + limits.maxSteps * (longestWait + pauses));
}

// Jitter can stretch a pause by jitterPct, never past capSec (retry-schedule jitteredDelayMs).
function worstPausesSec(policy: TRetrySchedulePolicy): number {
  let totalMs = 0;
  for (let attemptNo = 2; attemptNo <= policy.maxAttempts; attemptNo++) {
    const stretched = retryDelayMs(policy, attemptNo) * (1 + policy.jitterPct / 100);
    totalMs += Math.min(stretched, policy.capSec * 1000);
  }
  return totalMs / 1000;
}
